import React, { useLayoutEffect, useRef, useState } from 'react';
import { FieldRenderer } from './PublicRegistration';
import { registrationCompletionService as service } from '../../utils/api';
import { nationalIdError } from '../../utils/nationalId';
import { submissionToken } from '../../utils/formChannels';
import { Button } from '../../components/ui/button';

const unavailable = 'الرابط غير صالح أو انتهت صلاحيته. يرجى طلب رابط جديد من الكلية.';

export default function RegistrationCompletion() {
  const token = useRef(null);
  const opening = useRef(null);
  const requestId = useRef(null);
  const busy = useRef(false);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Capture only after commit: StrictMode can discard render-time refs.
  // Its effect replay reuses these refs and the same in-flight request.
  useLayoutEffect(() => {
    let active = true;
    if (token.current === null) {
      token.current = new URLSearchParams(window.location.hash.slice(1)).get('token') || '';
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
    if (!token.current) {
      setError(unavailable);
      setLoading(false);
      return undefined;
    }
    if (!opening.current) opening.current = service.open(token.current);
    opening.current.then(r => {
      if (!active) return;
      if (!r?.success) { setError(unavailable); return; }
      setForm(r.data);
      const preserved = new Set(r.data.preservedFields || []);
      setAnswers(Object.fromEntries(r.data.fields.map(f => [
        f.key, preserved.has(f.key) ? '' : (r.data.answers?.[f.key] ?? (f.type === 'checkbox' ? false : '')),
      ])));
    }).catch(() => { if (active) setError('تعذر الاتصال بالخادم. أعد فتح الرابط الأصلي للمحاولة مجدداً.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (busy.current) return;
    setError('');
    const payload = {};
    for (const field of form.fields) {
      const v = answers[field.key];
      const value = String(v ?? '').trim();
      if ((form.preservedFields || []).includes(field.key) && value === '') continue;
      if (field.required && (!value || (field.type === 'checkbox' && !v))) {
        setError(`الحقل "${field.label}" مطلوب`); return;
      }
      let invalid = '';
      if (value && (field.mapsTo === 'idNumber' || field.key === 'idnumber')) invalid = nationalIdError(value);
      else if (value && (field.mapsTo === 'email' || field.type === 'email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) invalid = 'أدخل بريداً إلكترونياً صحيحاً.';
      else if (value && (field.mapsTo === 'phone' || field.type === 'phone') && (!/^\+?[\d\s().-]+$/.test(value) || value.replace(/\D/g, '').length < 6 || value.replace(/\D/g, '').length > 15)) invalid = 'أدخل رقم هاتف صحيحاً.';
      else if (value && field.type === 'select' && !(field.options || []).includes(v)) invalid = 'اختر قيمة من الخيارات المتاحة.';
      else if (value && field.type === 'number' && !Number.isFinite(Number(value))) invalid = 'أدخل رقماً صحيحاً.';
      if (invalid) { setError(invalid); return; }
      payload[field.key] = v;
    }
    busy.current = true;
    setSubmitting(true);
    try {
      if (!requestId.current) requestId.current = submissionToken();
      const r = await service.submit(token.current, payload, requestId.current);
      if (r?.success) {
        setDone(true); setAnswers({}); setForm(null); token.current = ''; opening.current = null;
      } else setError(r?.message || unavailable);
    } catch {
      setError('تعذر حفظ البيانات. يرجى المحاولة مجدداً.');
    } finally { busy.current = false; setSubmitting(false); }
  }

  return (
    <main dir="rtl" className="min-h-[100dvh] bg-emerald-50 px-4 pt-safe pb-safe sm:py-10">
      <section className="max-w-2xl mx-auto rounded-xl border bg-white p-6 sm:p-10 shadow-sm space-y-5">
        <p className="text-emerald-700 font-semibold">كلية المدينة</p>
        <h1 className="text-2xl font-bold">{done ? 'تم تحديث بياناتك بنجاح' : (form?.title || 'استكمال بيانات التسجيل')}</h1>
        {done ? <p role="status">تم حفظ التفاصيل المحدّثة. لا يعني استكمال البيانات إنشاء حساب طالب أو الالتحاق التلقائي بالمساق.</p> : <>
          <p className="text-sm text-muted-foreground">رابط شخصي آمن صالح لمدة ٧ أيام من إنشائه. شاركه فقط مع الشخص المقصود.</p>
          {loading && <p role="status">جارٍ تحميل البيانات…</p>}
          {error && <p role="alert" className="bg-red-50 text-red-700 rounded p-3">{error}</p>}
          {form && <form noValidate onSubmit={submit} className="space-y-5">
            <fieldset disabled={submitting} className="min-w-0 w-full max-w-full space-y-5">
            {form.fields.map(field => <div key={field.key}>
              <FieldRenderer field={field} value={answers[field.key]} onChange={value => {
                setAnswers(old => ({ ...old, [field.key]: value }));
                requestId.current = null;
              }} />
              {(form.preservedFields || []).includes(field.key) && <p className="text-sm text-emerald-700 mt-1">محفوظ مسبقاً — اتركه فارغاً للاحتفاظ بالقيمة، أو أدخل التصحيح.</p>}
            </div>)}
            <p className="text-sm text-muted-foreground">تحديث البيانات لا يُنشئ حساباً أو التحاقاً تلقائياً.</p>
            <Button type="submit" disabled={submitting} className="w-full">{submitting ? 'جارٍ الحفظ…' : 'حفظ البيانات المحدّثة'}</Button>
            </fieldset>
          </form>}
        </>}
      </section>
    </main>
  );
}